import {
  Animate,
  CubeDraft,
  Hexagon3d,
  Image,
  Text,
  Texture,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { MATERIAL, MODEL_ID, VIEWER_SIDEBAR_TAB } from 'Pods/ThreeDStudio/Store/constants'

export const AI_TERMS_OF_SERVICE_HREF = 'https://www.cgtrader.com/pages/cgtrader-ai-terms-of-service'

// In-app studio destinations. Locale-agnostic on purpose: ContextBasedRouter mounts on the
// locale prefix, so Link and navigate() add it themselves.
export const STUDIO_PATH = {
  GENERATE: '/ai',
  IMAGE_GENERATOR: '/ai-image',
  COMMUNITY_THREE_D_MODELS: '/ai-3d-model-generator/3d-models',
  COMMUNITY_IMAGES: '/ai-image-generator/images',
  COMMUNITY_IMAGES_OBJECTS: '/ai-image-generator/gallery',
}

export const isCommunityImagesObjectPath = pathname => (
  String(pathname ?? '').startsWith(`${STUDIO_PATH.COMMUNITY_IMAGES_OBJECTS}/`)
)


// Destinations that only exist once `enable_2d_images_ideation_flow` is on. The controller is a
// fast page and cannot read the flag with an actor, so the routes always render the shell and the
// router bounces a non-enrolled visitor back to /ai.
export const IMAGE_PATH_PATTERN = `${STUDIO_PATH.IMAGE_GENERATOR}/:slug`

// One community image, opened in the gallery's modal over the gallery.
export const GALLERY_IMAGE_PATH_PATTERN = `${STUDIO_PATH.COMMUNITY_IMAGES}/:slug`

// A single route for the gallery and its images, so opening or closing an image does not
// remount the gallery and drop its filters, scroll position and loaded pages.
export const COMMUNITY_IMAGES_ROUTE = `${STUDIO_PATH.COMMUNITY_IMAGES}/:slug?`

// The per-object SEO views render the same gallery panel from a prefix of their own, which keeps
// their URLs clear of the single-image share links above.
export const COMMUNITY_IMAGES_OBJECT_PATTERN = `${STUDIO_PATH.COMMUNITY_IMAGES_OBJECTS}/:objectSlug`

export const TWO_D_IMAGES_PATHS = [
  STUDIO_PATH.IMAGE_GENERATOR,
  IMAGE_PATH_PATTERN,
  COMMUNITY_IMAGES_ROUTE,
  COMMUNITY_IMAGES_OBJECT_PATTERN,
]

// `edit=true` opens the image in the full-width editor instead of the lightbox.
export const IMAGE_EDIT_PARAM = 'edit'

export const hasImageEditParam = search => new URLSearchParams(search ?? '').get(IMAGE_EDIT_PARAM) === 'true'

const editSuffix = edit => (edit ? `?${IMAGE_EDIT_PARAM}=true` : '')

export const buildImagePath = (slug, { edit = false } = {}) => (
  `${STUDIO_PATH.IMAGE_GENERATOR}/${slug}${editSuffix(edit)}`
)

export const buildGalleryImagePath = (slug, { edit = false } = {}) => (
  `${STUDIO_PATH.COMMUNITY_IMAGES}/${slug}${editSuffix(edit)}`
)

// My Assets tab ids. The Images tab only exists behind `enable_2d_images_ideation_flow`.
export const LIBRARY_TAB = {
  MODELS: 'models',
  IMAGES: 'images',
}

export const iconSize = { height: '16px', width: '16px' }
export const iconSizeMedium = { height: '20px', width: '20px' }
export const iconSizeLarge = { height: '24px', width: '24px' }
// Loading/spinner glyphs in the AI Assistant (generating, search, mask editing).
export const iconSizeXLarge = { height: '40px', width: '40px' }

// Generation turn status for the AI Assistant Create tab conversation.
export const TURN_STATUS = { LOADING: 'loading', DONE: 'done', FAILED: 'failed' }

// Run statuses the backend never broadcasts past. Mirrors
// Generator::Run::TERMINAL_STATUSES; a run in any of these has already settled, so
// waiting on its websocket update would hang (ActionCable does not replay).
export const RUN_TERMINAL_STATUSES = new Set([
  'succeeded',
  'failed',
  'canceled',
  'timed_out',
  'nsfw_rejected',
])

// Brush-size bounds (px) for the AI Assistant Edit tab mask canvas.
export const BRUSH = { MIN: 4, MAX: 64, STEP: 1, DEFAULT: 24 }

// Maximum reference images attachable to a single AI Assistant prompt (Create + Edit).
export const MAX_ATTACHMENTS = 3

// Marks the controls that open the AI Assistant panel, so an already open panel
// treats a press on them as an inside interaction (see usePanelDismiss).
export const IMAGE_ASSISTANT_TRIGGER_SELECTOR = '[data-image-assistant-trigger]'

// Default environment ID for the viewer
export const DEFAULT_SURROUNDING_ID = 10 // "Studio Soft Light" in production db

// Default camera position and target for the viewer
export const DEFAULT_CAMERA_POSITION = { x: 0, y: 1.5, z: 4.5 }
export const DEFAULT_CAMERA_TARGET = { x: 0, y: 1, z: 0 }

// Default model auto-rotate speed for the viewer ([-100, 100])
export const DEFAULT_AUTO_ROTATE_SPEED = 3

// Conversion map for material to material mode for Tripo3D
export const MATERIAL_TO_MATERIAL_MODE = {
  [MATERIAL.BASIC]: 'shaded',
  [MATERIAL.PBR]: 'PBR',
  [MATERIAL.GEOMETRY_ONLY]: 'none',
}

export const VARIATION_TYPES = {
  [MODEL_ID.TENCENT_IMAGE_TO_3D_PRO]: { label: 'Image to 3D', icon: Image, isEnhancement: false },
  [MODEL_ID.TRIPO3D_IMAGE_TO_3D_DEFAULT]: { label: 'Image to 3D', icon: Image, isEnhancement: false },
  [MODEL_ID.MESHY_IMAGE_TO_3D_PRO]: { label: 'Image to 3D', icon: Image, isEnhancement: false },
  [MODEL_ID.HYPER3D_IMAGE_TO_3D_GEN2]: { label: 'Image to 3D', icon: Image, isEnhancement: false },
  [MODEL_ID.TENCENT_TEXT_TO_3D_PRO]: { label: 'Text to 3D', icon: Text, isEnhancement: false },
  [MODEL_ID.TRIPO3D_TEXT_TO_3D_DEFAULT]: { label: 'Text to 3D', icon: Text, isEnhancement: false },
  [MODEL_ID.MESHY_TEXT_TO_3D_PRO]: { label: 'Text to 3D', icon: Text, isEnhancement: false },
  [MODEL_ID.HYPER3D_TEXT_TO_3D_GEN2]: { label: 'Text to 3D', icon: Text, isEnhancement: false },
  [MODEL_ID.TENCENT_MESH_SIMPLIFICATION_PRO]: {
    label: 'Remesh', icon: Hexagon3d, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.REMESH,
  },
  [MODEL_ID.MESHY_MESH_SIMPLIFICATION_PRO]: {
    label: 'Remesh', icon: Hexagon3d, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.REMESH,
  },
  [MODEL_ID.TRIPO3D_MESH_SIMPLIFICATION_DEFAULT]: {
    label: 'Remesh', icon: Hexagon3d, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.REMESH,
  },
  [MODEL_ID.TENCENT_MESH_SEGMENTATION_PRO]: {
    label: 'Segment', icon: CubeDraft, isEnhancement: true, isSegmentation: true, tab: VIEWER_SIDEBAR_TAB.SEGMENT,
  },
  [MODEL_ID.TRIPO3D_MESH_SEGMENTATION_DEFAULT]: {
    label: 'Segment', icon: CubeDraft, isEnhancement: true, isSegmentation: true, tab: VIEWER_SIDEBAR_TAB.SEGMENT,
  },
  [MODEL_ID.TENCENT_RETEXTURE_PRO]: {
    label: 'Texture', icon: Texture, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.TEXTURE,
  },
  [MODEL_ID.TRIPO3D_RETEXTURE_DEFAULT]: {
    label: 'Texture', icon: Texture, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.TEXTURE,
  },
  [MODEL_ID.MESHY_RETEXTURE_PRO]: {
    label: 'Texture', icon: Texture, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.TEXTURE,
  },
  [MODEL_ID.HYPER3D_RETEXTURE_PRO]: {
    label: 'Texture', icon: Texture, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.TEXTURE,
  },
  [MODEL_ID.MESHY_ANIMATION_PRO]: {
    label: 'Animate', icon: Animate, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.ANIMATE,
  },
  [MODEL_ID.TRIPO3D_ANIMATION_DEFAULT]: {
    label: 'Animate', icon: Animate, isEnhancement: true, tab: VIEWER_SIDEBAR_TAB.ANIMATE,
  },
}
